import React from "react";
import { Link } from "react-router-dom";
import logo from "./assetpal-logo.png";
import "./LegalPage.css";

function PrivacyPolicy() {
  return (
    <div className="legal-page">
      <nav className="legal-nav">
        <Link to="/" className="legal-nav-brand">
          <img src={logo} alt="AssetPal Logo" className="legal-nav-logo" />
          <span>AssetPal</span>
        </Link>
        <div className="legal-nav-links">
          <Link to="/terms" className="legal-nav-link">Terms of Service</Link>
          <Link to="/" className="legal-nav-cta">← Back to Home</Link>
        </div>
      </nav>

      <div className="legal-container">
        <div className="legal-header">
          <h1>Privacy Policy</h1>
          <p className="legal-effective">Effective Date: April 11, 2026</p>
          <p className="legal-subtitle">Your privacy matters to us. This policy explains how we collect, use, and protect your data.</p>
        </div>

        <div className="legal-content">

          <section className="legal-section">
            <h2>1. Introduction</h2>
            <p>
              AssetPal ("we," "us," or "our") is committed to protecting your personal information.
              This Privacy Policy describes how we collect, use, disclose, and safeguard your information
              when you use the AssetPal mobile application ("App") or website ("Site").
            </p>
            <p>
              By using AssetPal, you consent to the data practices described in this Privacy Policy.
              If you do not agree with the terms of this policy, please do not use the App or Site.
            </p>
          </section>

          <section className="legal-section">
            <h2>2. Information We Collect</h2>

            <h3>2.1 Information You Provide Directly</h3>
            <ul>
              <li><strong>Account Information:</strong> When you register, we collect your email address and any profile information you provide.</li>
              <li><strong>Asset Data:</strong> Details about assets you add, including names, descriptions, purchase dates, values, serial numbers, and categories (home, vehicle, personal).</li>
              <li><strong>Documents and Images:</strong> Photos, warranty documents, receipts, and manuals you upload to the App.</li>
              <li><strong>Maintenance Records:</strong> Notes, reminders, and service records you log.</li>
            </ul>

            <h3>2.2 Information Collected Automatically</h3>
            <ul>
              <li><strong>Device Information:</strong> Device type, operating system version, and unique device identifiers.</li>
              <li><strong>Usage Data:</strong> Features used, screens viewed, and interactions within the App (e.g., crash logs and performance data).</li>
              <li><strong>Log Data:</strong> IP address, access times, and referring URLs when you access our Site.</li>
            </ul>

            <h3>2.3 Information from Third Parties</h3>
            <ul>
              <li><strong>Authentication:</strong> If you sign in via a third-party provider (e.g., Apple Sign In), we receive limited profile information from that provider as permitted by your privacy settings.</li>
              <li><strong>Subscription Data:</strong> RevenueCat provides us with subscription status and transaction history (not full payment card details).</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>3. How We Use Your Information</h2>
            <p>We use the information we collect to:</p>
            <ul>
              <li>Provide, operate, and improve the AssetPal service</li>
              <li>Create and manage your account</li>
              <li>Sync your asset data across devices</li>
              <li>Generate AI-powered maintenance reminders and insights</li>
              <li>Process subscription payments and manage billing</li>
              <li>Send transactional emails (e.g., password resets, subscription confirmations)</li>
              <li>Respond to your support requests and communications</li>
              <li>Monitor and analyze usage patterns to improve the App</li>
              <li>Detect and prevent fraud, abuse, and security incidents</li>
              <li>Comply with legal obligations</li>
            </ul>
            <p>
              We do not sell your personal data to third parties. We do not use your asset data for
              advertising purposes.
            </p>
          </section>

          <section className="legal-section">
            <h2>4. Data Sharing and Disclosure</h2>
            <p>We may share your information only in the following circumstances:</p>

            <h3>4.1 Service Providers</h3>
            <p>
              We share data with trusted third-party service providers who assist us in operating the
              App, subject to confidentiality agreements:
            </p>
            <ul>
              <li><strong>Supabase:</strong> Cloud database hosting, authentication, and secure file storage. Data is stored on Supabase infrastructure. <a href="https://supabase.com/privacy" target="_blank" rel="noopener noreferrer">Supabase Privacy Policy</a></li>
              <li><strong>RevenueCat:</strong> Subscription and in-app purchase management. <a href="https://www.revenuecat.com/privacy" target="_blank" rel="noopener noreferrer">RevenueCat Privacy Policy</a></li>
            </ul>

            <h3>4.2 Legal Requirements</h3>
            <p>
              We may disclose your information if required to do so by law or in response to a valid
              legal request (e.g., subpoena, court order, or government request).
            </p>

            <h3>4.3 Business Transfers</h3>
            <p>
              In the event of a merger, acquisition, or sale of all or a portion of our assets, your
              information may be transferred as part of that transaction. We will notify you of any
              such change.
            </p>

            <h3>4.4 Protection of Rights</h3>
            <p>
              We may share information when we believe disclosure is necessary to protect the rights,
              property, or safety of AssetPal, our users, or the public.
            </p>
          </section>

          <section className="legal-section">
            <h2>5. Data Security</h2>
            <p>
              We implement industry-standard security measures to protect your personal information,
              including:
            </p>
            <ul>
              <li>Encryption of data in transit (TLS/HTTPS)</li>
              <li>Encryption of data at rest via Supabase</li>
              <li>Secure authentication with password hashing</li>
              <li>Row-level security policies on our database</li>
              <li>Regular security reviews and access controls</li>
            </ul>
            <p>
              While we strive to protect your information, no method of transmission over the Internet
              or electronic storage is 100% secure. We cannot guarantee absolute security.
            </p>
          </section>

          <section className="legal-section">
            <h2>6. Data Retention</h2>
            <p>
              We retain your personal information for as long as your account is active or as needed
              to provide you with the service. When you delete your account:
            </p>
            <ul>
              <li>Your account data and asset records will be deleted from our active systems.</li>
              <li>Some data may be retained in backups for a limited period (typically up to 90 days) before being permanently purged.</li>
              <li>We may retain certain data as required by law or for legitimate business purposes such as fraud prevention.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>7. Your Rights and Choices</h2>
            <p>Depending on your location, you may have the following rights regarding your personal data:</p>
            <ul>
              <li><strong>Access:</strong> Request a copy of the personal data we hold about you.</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete data.</li>
              <li><strong>Deletion:</strong> Request deletion of your personal data ("right to be forgotten").</li>
              <li><strong>Portability:</strong> Request your data in a portable, machine-readable format.</li>
              <li><strong>Objection:</strong> Object to or restrict certain processing of your data.</li>
              <li><strong>Withdraw Consent:</strong> Where processing is based on consent, you may withdraw it at any time.</li>
            </ul>
            <p>
              To exercise any of these rights, please contact us at{" "}
              <a href="mailto:support@assetpal.org">support@assetpal.org</a>. We will respond to
              your request within 30 days.
            </p>
            <p>
              You may delete your account and associated data directly from within the AssetPal App
              under Settings → Account → Delete Account.
            </p>
          </section>

          <section className="legal-section">
            <h2>8. Children's Privacy</h2>
            <p>
              AssetPal is not directed to children under the age of 13. We do not knowingly collect
              personal information from children under 13. If we become aware that we have collected
              personal data from a child under 13 without parental consent, we will take steps to
              delete that information promptly.
            </p>
            <p>
              If you believe we have inadvertently collected information from a child, please contact
              us at <a href="mailto:support@assetpal.org">support@assetpal.org</a>.
            </p>
          </section>

          <section className="legal-section">
            <h2>9. California Privacy Rights (CCPA)</h2>
            <p>
              If you are a California resident, you have the following additional rights under the
              California Consumer Privacy Act (CCPA):
            </p>
            <ul>
              <li>The right to know what personal information we collect, use, disclose, and sell</li>
              <li>The right to delete personal information we have collected about you</li>
              <li>The right to opt-out of the sale of personal information (we do not sell your data)</li>
              <li>The right to non-discrimination for exercising your CCPA rights</li>
            </ul>
            <p>
              To submit a CCPA request, contact us at{" "}
              <a href="mailto:support@assetpal.org">support@assetpal.org</a>.
            </p>
          </section>

          <section className="legal-section">
            <h2>10. International Users</h2>
            <p>
              AssetPal is operated from the United States. If you are accessing the service from
              outside the United States, please be aware that your information may be transferred to,
              stored, and processed in the United States where our servers are located. By using
              AssetPal, you consent to this transfer.
            </p>
          </section>

          <section className="legal-section">
            <h2>11. Push Notifications</h2>
            <p>
              With your permission, we may send push notifications to your device to provide
              maintenance reminders and app updates. You can disable push notifications at any time
              through your device settings.
            </p>
          </section>

          <section className="legal-section">
            <h2>12. Third-Party Links</h2>
            <p>
              The App or Site may contain links to third-party websites or services. We are not
              responsible for the privacy practices or content of those third parties. We encourage
              you to review their privacy policies before providing any personal information.
            </p>
          </section>

          <section className="legal-section">
            <h2>13. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any material
              changes by updating the "Effective Date" at the top of this page. We encourage you to
              review this policy periodically to stay informed about how we protect your information.
            </p>
          </section>

          <section className="legal-section">
            <h2>14. Contact Us</h2>
            <p>
              If you have any questions, concerns, or requests regarding this Privacy Policy or your
              personal data, please contact us:
            </p>
            <div className="legal-contact-card">
              <p><strong>AssetPal</strong></p>
              <p>Email: <a href="mailto:support@assetpal.org">support@assetpal.org</a></p>
              <p>Website: <a href="https://assetpal.org">https://assetpal.org</a></p>
            </div>
          </section>

        </div>

        <div className="legal-footer-links">
          <Link to="/terms">Terms of Service</Link>
          <span>·</span>
          <Link to="/">Home</Link>
        </div>
      </div>

      <footer className="legal-footer">
        <p>© {new Date().getFullYear()} AssetPal. All rights reserved.</p>
      </footer>
    </div>
  );
}

export default PrivacyPolicy;
